import React, { useRef, useMemo, useCallback } from "react";

import useRecruitsGet from "../hooks/useRecruitsGet";
import EditRecruitModal from "../components/Modals/UpdateRecruitModal";
import useRecruitsUpdate from "../hooks/useRecruitsUpdate";
import { defaultRecruitData } from "../utils/defaultData";
import EnhancedTableControlPanel from "../components/Table/EnhancedTableControlPanel";
import {
  handleFilterChange,
  prepareFilterOptions,
} from "../utils/TableFilterService";
import "../css/ColumnFunctionsBar.css";
import "../css/TableResizer.css";
import "../css/Table.css";
import ColumnMenuDropdown from "../components/Dropdowns/ColumnMenuDropdown";
import { useColumnResizing } from "../hooks/useColumnResizing";
import { useTableCellEditing } from "../hooks/useTableCellEditing";
import { useColumnMenu } from "../hooks/useColumnMenu";
import { useColumnReordering } from "../hooks/useColumnReordering";
import { useControlPanelHover } from "../hooks/useControlPanelHover";
import { useRowEditingModal } from "../hooks/useRowEditingModal";
// import useTableState from "../hooks/useTableState"; // REMOVED
import useTableConfiguration from "../hooks/useTableConfiguration";
import useCellUpdateHandler from "../hooks/useCellUpdateHandler";
import TableHeader from "../components/Table/TableHeader";
import TableContent from "../components/Table/TableContent";
import { formatDateForInput } from "../utils/formatDateForInput";
import { FunctionBar } from "../components/Table/FunctionBar";
import { useTableContext } from "../hooks/useTableContext";

/**
 * BaseTable Component - A comprehensive table component for displaying and managing recruit data
 *
 * This component serves as the main orchestrator for complex table functionality including:
 * - Data fetching and display
 * - Column resizing, reordering, and pinning
 * - Cell editing with field mapping and validation
 * - Filtering and sorting
 * - Hover-based control panels
 * - Modal-based row editing
 *
 * @component
 * @example
 * ```jsx
 * <BaseTable
 *   tableConfig={recruitTableConfig}
 *   formatData={(recruits) => recruits.map(formatRecruitData)}
 *   tableId="recruits-table"
 *   dropdownData={{ companies, teams, departments, positions }}
 *   setterFunctions={{ setSelectedCompanies, setSelectedTeams }}
 * />
 * ```
 *
 * @param {Object} props - Component props
 * @param {Object} props.tableConfig - TanStack table configuration object containing column definitions
 * @param {Function} props.formatData - Function to format raw recruit data for table display
 * @param {string} [props.tableId="base-table"] - Unique identifier for the table instance
 * @param {string[]} [props.columnOrder=[]] - Initial column order array
 * @param {Object} [props.dropdownData={}] - Data for dropdown filters (companies, teams, etc.)
 * @param {Object} [props.setterFunctions={}] - Functions for updating external filter state
 * @param {string} [props.className=""] - Additional CSS classes for the table container
 *
 */
const BaseTable = ({
  tableConfig,
  formatData,
  tableId = "base-table",
  columnOrder: initialColumnOrder = [],
  dropdownData = {},
  setterFunctions = {},
  className = "",
}) => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  /**
   * Unified table context for ALL table-related state and actions.
   * Manages: visibility, pagination, sorting, filters, column order,
   * column sizing, hover states, editing states, formulas, etc.
   */
  const { state: contextState, ...contextActions } = useTableContext();

  // Extract commonly used state values from the unified context
  const {
    // States previously from tableState (now in contextState)
    columnSizing, // Note: context also had columnSizing, ensure it's the one you intend to use. The merged one is more flexible.
    upperValue,
    lowerValue,
    lastValidHoveredColumn,
    isPanelSticky,
    isPanelHovered,
    controlsPosition,
    editingCell,
    tempValue,
    refetchRecruitsTrigger,
    formulasMapping,

    // States previously direc
    columnVisibility,
    currentPage,
    rowsPerPage,
    sorting,
    filters,
    activeColumns,
    columnConditions,
  } = contextState;

  // ============================================================================
  // REFS FOR PERFORMANCE AND DOM ACCESS
  // ============================================================================

  const tableContainerRef = useRef(null);
  const functionBarRef = useRef(null);

  // ============================================================================
  // DATA FETCHING AND PROCESSING
  // ============================================================================
  const { data, error, loading } = useRecruitsGet(
    currentPage,
    rowsPerPage,
    filters,
    sorting,
    refetchRecruitsTrigger // This now comes from the unified context
  );

  const triggerRecruitsRefetch = useCallback(() => {
    // contextActions now contains incrementRefetchTrigger
    contextActions.incrementRefetchTrigger();
  }, [contextActions]);

  const recruits = useMemo(() => data?.Recruits || [], [data?.Recruits]);
  const formattedData = useMemo(
    () => formatData(recruits),
    [recruits, formatData]
  );
  const allFilterOptions = useMemo(
    () => prepareFilterOptions(dropdownData),
    [dropdownData]
  );

  // ============================================================================
  // HOOK ORCHESTRATION
  // ============================================================================

  const {
    isModalOpen: isEditModalOpen,
    editingRow: editingRowData,
    startEditingRow: startEditingRow,
    closeModal: closeEditModal,
  } = useRowEditingModal(defaultRecruitData);

  const {
    isAnalyticsTable,
    effectiveColumnOrder,
    totalPinnedWidth,
    table,
    isColumnPinned,
    getPinnedColumnStyle,
    hasActiveFiltersOrSorting,
    handleTogglePredefinedColumns,
    applyFiltersToContext, // This function likely uses contextActions.setFilters
    applySortToContext, // This function likely uses contextActions.setSorting
    hideColumnInContext, // This function likely uses contextActions.setColumnVisibility
    pinColumnInContext, // This function likely uses contextActions.setPinnedColumns or togglePinnedColumn
    isUpdatingFromContext,
    isResizing,
  } = useTableConfiguration({
    tableConfig,
    formatData,
    tableId,
    initialColumnOrder,
    data,
    recruits,
    formattedData,
    contextState, // Pass the whole context state
    contextActions, // Pass all context actions
    // tableState and tableActions are removed as they are merged into context
    startEditingRow,
  });

  const {
    handleResizeStart: initColumnResize,
    isResizingActive,
    activeResizingColumnId,
  } = useColumnResizing({
    // Assuming columnSiz is the source of truth
    initialColumnSizing: columnSizing,
    // contextActions now contains setColumnSizing and updateColumnSizing
    onColumnSizingChange: contextActions.setColumnSizing, // Or updateColumnSizing depending on need
    // onContextColumnSizingChange is no longer needed as there's one source
    tableContainerRef,
  });

  const { updateRecruit } = useRecruitsUpdate();

  const handleCellUpdate = useCellUpdateHandler(
    updateRecruit,
    triggerRecruitsRefetch
  );

  const cellEditingManager = useTableCellEditing({
    onUpdateCell: handleCellUpdate,
    isAnalyticsTable,
    formattedData,
    // Pass contextActions for actions like setEditingCell, setTempValue, resetEditingCell
    tableActions: contextActions,
  });

  const columnMenuManager = useColumnMenu({
    onApplyFilter: applyFiltersToContext,
    onApplySort: applySortToContext,
    onHideColumn: hideColumnInContext,
    onPinColumn: pinColumnInContext,
  });

  const hoverManager = useControlPanelHover(
    functionBarRef,
    // contextActions now contains setControlsPosition and setLastValidHoveredColumn
    contextActions.setControlsPosition,
    contextActions.setLastValidHoveredColumn,
    tableContainerRef
  );

  const { handleDragStart, handleDragOver, handleDrop, handleDragEnd } =
    useColumnReordering(
      effectiveColumnOrder,
      isColumnPinned,
      contextActions.setColumnOrder // This was already correct
    );

  // ============================================================================
  // LOADING AND ERROR STATES
  // ============================================================================

  if (loading && !data) {
    return <div className="p-4 text-center">Loading...</div>;
  }

  if (error) {
    return <div className="p-4 text-center text-red-500">Error: {error}</div>;
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className={`relative w-full ${className}`}>
      <FunctionBar
        isPanelHovered={isPanelHovered}
        isPanelSticky={isPanelSticky}
        lastValidHoveredColumn={lastValidHoveredColumn}
        controlsPosition={controlsPosition}
        EnhancedTableControlPanel={EnhancedTableControlPanel}
        handleTogglePredefinedColumns={handleTogglePredefinedColumns}
        hoverManager={hoverManager}
        activeColumns={activeColumns}
        contextActions={contextActions} // Pass all context actions
        setterFunctions={setterFunctions}
        columnVisibility={columnVisibility}
        allFilterOptions={allFilterOptions}
        filters={filters}
        isColumnPinned={isColumnPinned}
        tableConfig={tableConfig}
        functionBarRef={functionBarRef}
        tableId={tableId}
      />

      <div
        className="table-container w-full"
        ref={tableContainerRef}
        style={{ overflowY: "scroll" }}
      >
        <div className="w-full overflow-x-visible">
          <table className="min-w-full divide-y divide-gray-200">
            <TableHeader
              table={table}
              activeColumns={activeColumns}
              isColumnPinned={isColumnPinned}
              getPinnedColumnStyle={getPinnedColumnStyle}
              isAnalyticsTable={isAnalyticsTable}
              hoverManager={hoverManager}
              handleDragStart={handleDragStart}
              handleDragOver={handleDragOver}
              handleDragEnd={handleDragEnd}
              handleDrop={handleDrop}
              initColumnResize={initColumnResize}
              isResizingActive={isResizingActive}
              activeResizingColumnId={activeResizingColumnId}
              hasActiveFiltersOrSorting={hasActiveFiltersOrSorting}
              columnMenuManager={columnMenuManager}
              hoveredColumn={hoverManager.hoveredColumn} // This likely comes from hoverManager's internal state or context
              isPanelHovered={isPanelHovered}
              isPanelSticky={isPanelSticky}
              lastValidHoveredColumn={lastValidHoveredColumn}
              controlsPosition={controlsPosition}
              columnVisibility={columnVisibility}
              contextActions={contextActions} // Pass all context actions
              setterFunctions={setterFunctions}
              allFilterOptions={allFilterOptions}
              filters={filters}
              handleTogglePredefinedColumns={handleTogglePredefinedColumns}
            />
            <TableContent
              table={table}
              activeColumns={activeColumns}
              isColumnPinned={isColumnPinned}
              getPinnedColumnStyle={getPinnedColumnStyle}
              columnConditions={columnConditions}
              isAnalyticsTable={isAnalyticsTable}
              upperValue={upperValue}
              lowerValue={lowerValue}
              formatDateForInput={formatDateForInput}
              cellEditingManager={cellEditingManager} // Ensure this uses contextActions internally if needed
              dropdownData={dropdownData}
            />
          </table>
        </div>
      </div>

      {isEditModalOpen && editingRowData && (
        <EditRecruitModal
          isOpen={isEditModalOpen}
          onClose={() => {
            closeEditModal();
            // If resetting editing cell state was tied to modal close, ensure it's handled
            // contextActions.resetEditingCell(); // Example if needed
          }}
          recruitData={editingRowData}
          updateRecruit={updateRecruit}
        />
      )}

      {columnMenuManager.openMenuColumnId && (
        <div
          className="fixed z-50 column-menu-dropdown-container"
          style={{
            top: `${columnMenuManager.menuPosition.top}px`,
            left: `${columnMenuManager.menuPosition.left}px`,
          }}
        >
          <ColumnMenuDropdown
            isOpen={columnMenuManager.openMenuColumnId}
            onClose={columnMenuManager.closeMenu}
            onSort={columnMenuManager.menuActionHandlers.onSort}
            onHide={columnMenuManager.menuActionHandlers.onHide}
            onPin={columnMenuManager.menuActionHandlers.onPin}
            onFilter={(ids, colId) =>
              handleFilterChange(
                colId, // columnId
                ids, // selected IDs array
                true, // isApply
                filters, // currentFilters from props
                contextActions.setFilters, // updateFunction from props
                setterFunctions // your map of setSelectedX()
              )
            }
            isPinned={isColumnPinned(columnMenuManager.openMenuColumnId)}
            hasFilters={!!allFilterOptions[columnMenuManager.openMenuColumnId]}
            columnId={columnMenuManager.openMenuColumnId}
            filterProps={{
              options:
                allFilterOptions[columnMenuManager.openMenuColumnId] || [],
              currentFilters: filters,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default BaseTable;
